// LogoutPage — controlled sign-out flow at /logout.
//
// On mount: clears auth state and POSTs to /api/auth/logout (best-effort).
// After done: shows "Successfully logged out" then redirects to /login after 2s.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearToken, clearSession } from '@/shared/auth';
import { AuthLayout } from '../auth/AuthLayout';

type LogoutState = 'pending' | 'done';

export function LogoutPage() {
  const [logoutState, setLogoutState] = useState<LogoutState>('pending');
  const navigate = useNavigate();

  useEffect(() => {
    // Clear any in-memory auth state immediately.
    clearToken();
    clearSession();

    // Call the BFF logout endpoint to revoke the token and clear the cookie.
    fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    })
      .catch(() => {
        // best-effort; session cookie is cleared server-side regardless
      })
      .finally(() => {
        setLogoutState('done');
      });
  }, []);

  useEffect(() => {
    if (logoutState !== 'done') return;
    const timer = window.setTimeout(() => {
      navigate('/login', { replace: true });
    }, 2000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [logoutState, navigate]);

  return (
    <AuthLayout headingId="logout-heading">
      <div className="flex flex-col items-center gap-3 text-center">
        {logoutState === 'pending' ? (
          <>
            <h1 id="logout-heading" className="text-xl font-semibold">
              Signing you out…
            </h1>
            <p>Please wait while we securely sign you out.</p>
          </>
        ) : (
          <>
            <h1 id="logout-heading" className="text-xl font-semibold">
              Successfully logged out
            </h1>
            <p>Redirecting you to the login page…</p>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
